import React, { useMemo } from "react";

import { Switch } from "@mui/material";
import { SelectOption } from "components/forms/Select/Select";
import { useCharacteristics, useGroupByContent, useSimpleMeasures } from "hooks";

import { useCalculatedSelector } from "../../../store";
import { ReportsQueries, RowBy } from "../../../store/types";
import { AutocompleteMultiSelect } from "../AutocompleteMultiSelect/AutocompleteMultiSelect";

import "./GroupByContent.styles.scss";

export type DataFormation = Partial<
  Pick<ReportsQueries, "groupBy" | "measures" | "rowBy" | "currency" | "exchangeMethod">
>;

export interface GroupByMenuSettings {
  showCharacteristics?: boolean;
  showColumns?: boolean;
  showGroupBy?: boolean;
  showRowBy?: boolean;
  showRiskMetrics?: boolean;
}

const rowByOptions: SelectOption<RowBy>[] = [
  { text: "Investor", key: "Investor", value: "investor" },
  { text: "Deal", key: "Deal", value: "deal" },
  { text: "Measure", key: "Measure", value: "measure" }
];

interface GroupByContentProps {
  showCharacteristics?: boolean;
  showGroupBy?: boolean;
  showOriginalCurrency?: boolean;
  showRowBy?: boolean;
  showRiskMetrics?: boolean;
  onChangeDataFormation?: (dataFormation: DataFormation) => void;
  title?: string;
}

export const GroupByContent: React.FC<GroupByContentProps> = ({
  showCharacteristics = false,
  showGroupBy = true,
  showOriginalCurrency = false,
  showRowBy = true,
  showRiskMetrics = false,
  title = "Deal Measures Configuration",
  onChangeDataFormation
}) => {
  const characteristicsAll = useCharacteristics();
  const calculatedFieldsAll = useCalculatedSelector();

  const simpleMeasures = useSimpleMeasures();

  const fullCharacteristics: (SelectOption | string)[] = useMemo(() => {
    return [
      { text: "Investor", key: "Investor", value: "investor" },
      { text: "Deal", key: "Deal", value: "deal" },
      // { text: "Asset Class", key: "Asset Class", value: "assetClass" },
      { text: "Measure", key: "Measure", value: "measure" },
      ...characteristicsAll
    ];
  }, [characteristicsAll]);

  const fields: Array<keyof ReportsQueries> = ["measures", "rowBy", "originalCurrency"];
  showCharacteristics && fields.push("characteristics");
  showOriginalCurrency && fields.push("originalCurrency");
  showRiskMetrics && fields.push("calculatedFields");
  showRiskMetrics && fields.push("rowBy");

  const { setFieldValue, values } = useGroupByContent({
    fields,
    onChangeDataFormation,
    groupByFieldName: showGroupBy ? "groupBy" : undefined
  });

  return (
    <div className="group-by-content">
      <div className="group-by-content__title">{title}</div>
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={values.measures}
            label="Measures"
            options={simpleMeasures}
            multiple
            limitTags={2}
            onChange={(value) => setFieldValue("measures", value)}
          />
        </div>
      </div>
      {showRiskMetrics && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMultiSelect
              defaultValue={values.calculatedFields}
              multiple
              label="Calculated Measures"
              options={calculatedFieldsAll}
              limitTags={2}
              onChange={(value) => setFieldValue("calculatedFields", value)}
            />
          </div>
        </div>
      )}
      {showCharacteristics && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMultiSelect
              defaultValue={values.characteristics}
              label="Characteristics"
              multiple
              options={characteristicsAll}
              limitTags={2}
              onChange={(value) => setFieldValue("characteristics", value)}
            />
          </div>
        </div>
      )}
      {(showGroupBy || showOriginalCurrency || showRowBy) && (
        <div className="group-by-content__title">View Configuration</div>
      )}

      {showOriginalCurrency && (
        <div className="row">
          <div className="input-wrap">
            <span className="input-wrap__title">Original Currency</span>
            <Switch
              checked={values.originalCurrency}
              // onChange={(e, checked) => setQueriesAction({ originalCurrency: checked })}
              onChange={(e, checked) => {
                setFieldValue("originalCurrency", checked);
              }}
            />
          </div>
        </div>
      )}
      {showGroupBy && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMultiSelect
              defaultValue={values.firstGroupBy}
              label="Group By"
              options={fullCharacteristics}
              onChange={(value) => setFieldValue("firstGroupBy", value)}
            />
          </div>
        </div>
      )}
      {showGroupBy && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMultiSelect
              defaultValue={values.secondGroupBy}
              label="Subgroup By"
              options={fullCharacteristics}
              onChange={(value) => setFieldValue("secondGroupBy", value)}
            />
          </div>
        </div>
      )}
      {showRowBy && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMultiSelect
              defaultValue={values.rowBy}
              label="View By"
              disabledClean
              options={rowByOptions}
              onChange={(value) => setFieldValue("rowBy", value)}
            />
          </div>
        </div>
      )}
      {/* <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={values.currency}
            label="Currency"
            options={currenciesArray}
            onChange={(value) => setFieldValue("currency", value)}
            disabledClean
          />
        </div>
      </div>

      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={values.exchangeMethod}
            label="Hedging"
            options={hedgingItems}
            onChange={(value) => setFieldValue("exchangeMethod", value)}
            disabledClean
          />
        </div>
      </div> */}
    </div>
  );
};
